import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { DragDropContext, Droppable, Draggable, type DropResult } from '@hello-pangea/dnd'
import { useTranslation } from '../hooks/useTranslation'
import { authFetchJson } from '../lib/authFetch'
import { DOMAIN_BADGE_CLASSES, DOMAIN_CHIP_ACTIVE_CLASSES, DOMAIN_REGISTRY, type Domain } from '../lib/domainRegistry'
import { PIPELINE_STAGES, PIPELINE_MAP, isTransitionAllowed, type PipelineStage } from '../lib/pipelineRegistry'
import { ProjectDetail } from '../components/ProjectDetail'

// ─── Types ────────────────────────────────────────────────────────────────

interface TeamMember {
  id: number
  name: string
  email: string
  picture_url: string | null
}

interface MiniCard {
  id: number
  title: string
  domain: string | null
  score_composite: number | null
  owner_name: string | null
  days_in_stage: number
  vote_tally: number
}

interface PipelineStageData {
  stage: PipelineStage
  label: string
  count: number
  projects: MiniCard[]
  has_more: boolean
}

interface PipelineBoardResponse {
  stages: PipelineStageData[]
}

interface TransitionResponse {
  transition: Record<string, unknown>
  project: {
    id: number
    pipeline_stage: PipelineStage
    stage_entered_at: string
  }
}

// ─── Mini Card Component ──────────────────────────────────────────────────

function MiniCardComponent({ card, onClick }: { card: MiniCard; onClick?: (id: number) => void }) {
  const domainConfig =
    card.domain && DOMAIN_REGISTRY[card.domain as Domain]
      ? DOMAIN_REGISTRY[card.domain as Domain]
      : null

  const scorePercent = card.score_composite ? Math.round(card.score_composite * 100) : 0
  let scoreColor = 'bg-red-500'
  if (scorePercent >= 70) scoreColor = 'bg-green-500'
  else if (scorePercent >= 40) scoreColor = 'bg-yellow-500'

  return (
    <div
      className="bg-bg-surface hover:bg-bg-surface-hover border border-border-subtle rounded-lg p-3 transition-colors duration-150 cursor-pointer"
      onClick={(e) => {
        // Don't open detail if we're dragging
        if (onClick) {
          e.stopPropagation()
          onClick(card.id)
        }
      }}
    >
      {/* Domain badge */}
      {domainConfig && (
        <div className="flex items-center gap-2 mb-2">
          <span className={`inline-block px-2 py-1 rounded text-caption-xs font-semibold whitespace-nowrap ${DOMAIN_BADGE_CLASSES[card.domain as Domain]}`}>
            {domainConfig.label}
          </span>
        </div>
      )}

      {/* Title */}
      <h3 className="text-body-sm font-semibold text-text-primary line-clamp-2 mb-2">
        {card.title}
      </h3>

      {/* Score bar */}
      {card.score_composite !== null && (
        <div className="flex items-center gap-2 mb-2">
          <div className="w-12 h-1 bg-bg-body rounded-full overflow-hidden flex-shrink-0">
            <div
              className={`h-full ${scoreColor} transition-all duration-300`}
              style={{ width: `${scorePercent}%` }}
            />
          </div>
          <span className="text-caption-xs text-text-secondary whitespace-nowrap">
            {scorePercent}%
          </span>
        </div>
      )}

      {/* Owner & Days */}
      <div className="flex items-center justify-between text-caption-xs text-text-secondary mb-2">
        <span>👤 {card.owner_name || 'Unassigned'}</span>
        <span>⏱ {card.days_in_stage}d</span>
      </div>

      {/* Vote tally */}
      <div className="flex items-center gap-1 text-caption-xs">
        <span className={card.vote_tally > 0 ? 'text-green-400' : card.vote_tally < 0 ? 'text-red-400' : 'text-text-secondary'}>
          ▲ {Math.max(0, card.vote_tally)} ▼ {Math.max(0, -card.vote_tally)}
        </span>
      </div>
    </div>
  )
}

// ─── Skeleton Column ──────────────────────────────────────────────────────

function SkeletonColumn() {
  return (
    <div className="flex-shrink-0 w-56 flex flex-col gap-2">
      <div className="h-4 bg-bg-surface rounded animate-pulse" />
      <div className="space-y-3">
        {[0, 1, 2].map(i => (
          <div key={i} className="bg-bg-surface rounded-lg h-32 animate-pulse" />
        ))}
      </div>
    </div>
  )
}

// ─── Transition Reason Modal ──────────────────────────────────────────────

interface ReasonModalProps {
  isOpen: boolean
  projectTitle: string
  stageLabel: string
  onConfirm: (reason: string) => Promise<void>
  onCancel: () => void
  isSubmitting: boolean
}

function ReasonModal({ isOpen, projectTitle, stageLabel, onConfirm, onCancel, isSubmitting }: ReasonModalProps) {
  const [reason, setReason] = useState('')
  const { t } = useTranslation()

  if (!isOpen) return null

  const handleSubmit = async () => {
    await onConfirm(reason)
    setReason('')
  }

  const handleCancel = () => {
    setReason('')
    onCancel()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-bg-surface border border-border-standard rounded-lg p-6 w-96 shadow-2xl">
        <h2 className="text-h3 text-text-primary mb-4">
          {t('pipeline.modal.moving')} "{projectTitle}" {t('pipeline.modal.to')} {stageLabel}
        </h2>

        <label className="block mb-4">
          <span className="text-body-sm text-text-secondary mb-2 block">
            {t('pipeline.modal.reason')}
          </span>
          <textarea
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder={t('pipeline.modal.reason_placeholder')}
            className="w-full px-3 py-2 bg-bg-body border border-border-subtle rounded text-text-primary placeholder-text-tertiary focus:outline-none focus:border-border-standard focus:ring-1 focus:ring-border-standard resize-none"
            rows={4}
            disabled={isSubmitting}
          />
        </label>

        <div className="flex justify-end gap-3">
          <button
            onClick={handleCancel}
            disabled={isSubmitting}
            className="px-4 py-2 rounded text-text-secondary hover:bg-bg-body transition-colors disabled:opacity-50"
          >
            {t('pipeline.modal.cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || !reason.trim()}
            className="px-4 py-2 bg-pipeline-vb-process/60 hover:bg-pipeline-vb-process text-white rounded font-semibold transition-colors disabled:opacity-50"
          >
            {isSubmitting ? t('pipeline.modal.confirming') : t('pipeline.modal.confirm')}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Error Toast (simple alert) ────────────────────────────────────────────

interface ErrorToastProps {
  message: string
  onDismiss: () => void
}

function ErrorToast({ message, onDismiss }: ErrorToastProps) {
  useEffect(() => {
    const timer = setTimeout(onDismiss, 5000)
    return () => clearTimeout(timer)
  }, [onDismiss])

  return (
    <div className="fixed bottom-4 right-4 z-40 bg-red-900/80 border border-red-700 text-red-100 px-4 py-3 rounded-lg max-w-sm">
      <p className="text-body-sm">{message}</p>
    </div>
  )
}

// ─── Main Pipeline Page ────────────────────────────────────────────────────

export function PipelinePage() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  // Default hidden stages
  const DEFAULT_HIDDEN_STAGES = new Set<string>(['FUNDED', 'EXITED', 'PARKED', 'GRAVEYARD'])

  // State
  // Note: selectedProjectId is no longer used — see handoff S9-14 for migration to /projects/:id route
  // Keeping this for reference but it's unused after refactor below
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null)
  const [stages, setStages] = useState<PipelineStageData[]>([])
  const [loading, setLoading] = useState(true)
  const [hiddenDomains, setHiddenDomains] = useState<Set<string>>(new Set())
  const [hiddenStages, setHiddenStages] = useState<Set<string>>(DEFAULT_HIDDEN_STAGES)
  const [selectedPeople, setSelectedPeople] = useState<Set<number>>(new Set())
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([])
  const [stagesDropdownOpen, setStagesDropdownOpen] = useState(false)
  const [peopleDropdownOpen, setPeopleDropdownOpen] = useState(false)
  const [selectedOwner, setSelectedOwner] = useState<string>('')
  const [searchQuery, setSearchQuery] = useState('')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [reasonModal, setReasonModal] = useState<{
    isOpen: boolean
    projectId: number
    projectTitle: string
    fromStage: PipelineStage
    toStage: PipelineStage
  }>({
    isOpen: false,
    projectId: 0,
    projectTitle: '',
    fromStage: 'INBOX',
    toStage: 'PARKED',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [loadingMore, setLoadingMore] = useState<Record<string, boolean>>({})
  const stagesDropdownRef = useRef<HTMLDivElement>(null)
  const peopleDropdownRef = useRef<HTMLDivElement>(null)
  const [dragState, setDragState] = useState<{
    draggedCard: MiniCard | null
    fromStage: PipelineStage | null
  }>({ draggedCard: null, fromStage: null })

  // Fetch board data
  const fetchBoard = useCallback(async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (selectedOwner) params.set('owner', selectedOwner)
      if (searchQuery) params.set('q', searchQuery)

      const response = await authFetchJson<PipelineBoardResponse>(
        `/api/v2/pipeline/board?${params.toString()}`,
      )

      setStages(response.stages)
      setErrorMessage(null)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load pipeline board'
      setErrorMessage(message)
      setStages([])
    } finally {
      setLoading(false)
    }
  }, [selectedOwner, searchQuery])

  // Load more projects for a given stage
  const handleLoadMore = useCallback(async (stage: PipelineStage) => {
    setLoadingMore(prev => ({ ...prev, [stage]: true }))
    try {
      const stageData = stages.find(s => s.stage === stage)
      const offset = stageData?.projects.length ?? 0

      const params = new URLSearchParams()
      params.set('stage', stage)
      params.set('offset', String(offset))
      params.set('limit', '20')
      if (selectedOwner) params.set('owner', selectedOwner)
      if (searchQuery) params.set('q', searchQuery)

      const response = await authFetchJson<{ projects: MiniCard[]; has_more: boolean }>(
        `/api/v2/pipeline/board/more?${params.toString()}`,
      )

      setStages(prevStages =>
        prevStages.map(s => {
          if (s.stage === stage) {
            return {
              ...s,
              projects: [...s.projects, ...response.projects],
              has_more: response.has_more,
            }
          }
          return s
        }),
      )
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load more projects'
      setErrorMessage(message)
    } finally {
      setLoadingMore(prev => ({ ...prev, [stage]: false }))
    }
  }, [stages, selectedOwner, searchQuery])

  // Fetch on mount and filter change
  useEffect(() => {
    fetchBoard()
  }, [fetchBoard])

  // Fetch team members for People filter
  useEffect(() => {
    authFetchJson<{ team: TeamMember[] }>('/api/v2/team')
      .then(data => setTeamMembers(data.team || []))
      .catch(() => setTeamMembers([]))
  }, [])

  // Close dropdowns on click outside
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (stagesDropdownRef.current && !stagesDropdownRef.current.contains(e.target as Node)) {
        setStagesDropdownOpen(false)
      }
      if (peopleDropdownRef.current && !peopleDropdownRef.current.contains(e.target as Node)) {
        setPeopleDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  // Get visible stages (filter by hidden stages + hidden domains + selected people)
  // People filter: get set of project IDs that have selected people assigned
  const [peopleProjectIds, setPeopleProjectIds] = useState<Set<number> | null>(null)

  useEffect(() => {
    if (selectedPeople.size === 0) {
      setPeopleProjectIds(null)
      return
    }
    // Fetch assignments for selected people
    const userIds = Array.from(selectedPeople)
    const params = new URLSearchParams()
    userIds.forEach(id => params.append('user_id', String(id)))

    authFetchJson<{ project_ids: number[] }>(`/api/v2/team/project-ids?${params.toString()}`)
      .then(data => setPeopleProjectIds(new Set(data.project_ids || [])))
      .catch(() => setPeopleProjectIds(null))
  }, [selectedPeople])

  const visibleStages = useMemo(
    () =>
      stages
        .filter(s => !hiddenStages.has(s.stage))
        .map(s => {
          let projects = s.projects
          if (hiddenDomains.size > 0) {
            projects = projects.filter(p => !p.domain || !hiddenDomains.has(p.domain))
          }
          if (peopleProjectIds) {
            projects = projects.filter(p => peopleProjectIds.has(p.id))
          }
          return { ...s, projects, count: projects.length }
        }),
    [stages, hiddenStages, hiddenDomains, peopleProjectIds],
  )

  // Handle drag end
  const handleDragEnd = useCallback(
    async (result: DropResult) => {
      const { source, destination, draggableId } = result

      // Dropped outside or same position
      if (!destination || (source.droppableId === destination.droppableId && source.index === destination.index)) {
        return
      }

      const projectId = parseInt(draggableId, 10)
      const fromStage = source.droppableId as PipelineStage
      const toStage = destination.droppableId as PipelineStage

      // Validate transition against allowed transitions
      if (!isTransitionAllowed(fromStage, toStage)) {
        setErrorMessage(
          `Cannot move from ${PIPELINE_MAP[fromStage]?.label ?? fromStage} to ${PIPELINE_MAP[toStage]?.label ?? toStage}`,
        )
        return
      }

      // Find the card
      const sourceStageData = stages.find(s => s.stage === fromStage)
      const card = sourceStageData?.projects.find(p => p.id === projectId)

      if (!card) return

      // Optimistic UI: remove from source, add to destination
      setStages(prevStages =>
        prevStages.map(s => {
          if (s.stage === fromStage) {
            return {
              ...s,
              projects: s.projects.filter(p => p.id !== projectId),
              count: s.count - 1,
            }
          }
          if (s.stage === toStage) {
            return {
              ...s,
              projects: [...s.projects.slice(0, destination.index), card, ...s.projects.slice(destination.index)],
              count: s.count + 1,
            }
          }
          return s
        }),
      )

      setDragState({ draggedCard: card, fromStage })

      // If moving to PARKED or GRAVEYARD, ask for reason
      if (toStage === 'PARKED' || toStage === 'GRAVEYARD') {
        setReasonModal({
          isOpen: true,
          projectId,
          projectTitle: card.title,
          fromStage,
          toStage,
        })
        return
      }

      // Otherwise, execute transition immediately
      try {
        setIsSubmitting(true)
        await authFetchJson<TransitionResponse>(`/api/v2/projects/${projectId}/transitions`, {
          method: 'POST',
          json: { to_stage: toStage },
        })
        setErrorMessage(null)
      } catch (err) {
        // Revert optimistic update
        setStages(prevStages =>
          prevStages.map(s => {
            if (s.stage === fromStage) {
              return {
                ...s,
                projects: [...s.projects.slice(0, source.index), card, ...s.projects.slice(source.index)],
                count: s.count + 1,
              }
            }
            if (s.stage === toStage) {
              return {
                ...s,
                projects: s.projects.filter(p => p.id !== projectId),
                count: s.count - 1,
              }
            }
            return s
          }),
        )

        const message = err instanceof Error ? err.message : 'Failed to transition project'
        setErrorMessage(message)
      } finally {
        setIsSubmitting(false)
        setDragState({ draggedCard: null, fromStage: null })
      }
    },
    [stages],
  )

  // Handle reason modal confirmation
  const handleReasonConfirm = useCallback(
    async (reason: string) => {
      const { projectId, fromStage, toStage } = reasonModal

      try {
        setIsSubmitting(true)
        await authFetchJson<TransitionResponse>(`/api/v2/projects/${projectId}/transitions`, {
          method: 'POST',
          json: { to_stage: toStage, reason },
        })
        setReasonModal({ ...reasonModal, isOpen: false })
        setErrorMessage(null)
      } catch (err) {
        // Revert optimistic update
        const card = dragState.draggedCard
        if (card) {
          setStages(prevStages =>
            prevStages.map(s => {
              if (s.stage === fromStage) {
                return {
                  ...s,
                  projects: [...s.projects, card],
                  count: s.count + 1,
                }
              }
              if (s.stage === toStage) {
                return {
                  ...s,
                  projects: s.projects.filter(p => p.id !== projectId),
                  count: s.count - 1,
                }
              }
              return s
            }),
          )
        }

        const message = err instanceof Error ? err.message : 'Failed to transition project'
        setErrorMessage(message)
      } finally {
        setIsSubmitting(false)
        setDragState({ draggedCard: null, fromStage: null })
      }
    },
    [reasonModal, dragState],
  )

  const handleReasonCancel = useCallback(() => {
    const { projectId, fromStage, toStage } = reasonModal
    const card = dragState.draggedCard

    // Revert optimistic update
    if (card) {
      setStages(prevStages =>
        prevStages.map(s => {
          if (s.stage === fromStage) {
            return {
              ...s,
              projects: [...s.projects, card],
              count: s.count + 1,
            }
          }
          if (s.stage === toStage) {
            return {
              ...s,
              projects: s.projects.filter(p => p.id !== projectId),
              count: s.count - 1,
            }
          }
          return s
        }),
      )
    }

    setReasonModal({ ...reasonModal, isOpen: false })
    setDragState({ draggedCard: null, fromStage: null })
  }, [reasonModal, dragState])

  // Get unique domains for filter chips
  const availableDomains = useMemo(() => {
    const domainSet = new Set<string>()
    stages.forEach(s =>
      s.projects.forEach(p => {
        if (p.domain) domainSet.add(p.domain)
      }),
    )
    return Array.from(domainSet).sort()
  }, [stages])

  // Pipeline board always shown; detail is handled via /projects/:id route
  // (Keeping the selectedProjectId state unused for backward-compat, but not rendering the modal)

  return (
    <div className="h-full flex flex-col bg-bg-body">
      {/* Header */}
      <div className="border-b border-border-subtle px-6 py-4">
        {/* Filters row */}
        <div className="flex gap-3 items-center flex-wrap">
          {/* Domain chips (inverse toggle: click to HIDE) */}
          <div className="flex gap-2 flex-wrap">
            {availableDomains.map(domain => {
              const domainConfig = DOMAIN_REGISTRY[domain as Domain]
              const isHidden = hiddenDomains.has(domain)
              const chipClass = isHidden
                ? 'bg-bg-surface text-text-tertiary border-border-subtle opacity-50'
                : domainConfig
                  ? DOMAIN_CHIP_ACTIVE_CLASSES[domain as Domain]
                  : 'bg-bg-surface text-text-secondary border-border-subtle'

              return (
                <button
                  key={domain}
                  onClick={() => {
                    setHiddenDomains(prev => {
                      const next = new Set(prev)
                      if (next.has(domain)) {
                        next.delete(domain)
                      } else {
                        next.add(domain)
                      }
                      return next
                    })
                  }}
                  className={`px-3 py-1 rounded-full text-caption-xs font-semibold transition-colors border ${chipClass}`}
                >
                  {domainConfig?.icon} {domainConfig?.label || domain}
                </button>
              )
            })}
          </div>

          {/* Stages dropdown — pill style, inline with domain chips */}
          <div className="relative" ref={stagesDropdownRef}>
            <button
              onClick={() => { setStagesDropdownOpen(v => !v); setPeopleDropdownOpen(false) }}
              className={`px-3 py-1 rounded-full text-caption-xs font-semibold transition-colors border flex items-center gap-1.5 ${
                hiddenStages.size !== PIPELINE_STAGES.length
                  ? 'bg-[#2a2a44] text-text-primary border-[#3a3a54] hover:bg-[#333350]'
                  : 'bg-bg-surface text-text-tertiary border-border-subtle opacity-50 hover:opacity-75'
              }`}
            >
              Stages
              <span className="text-text-tertiary text-[10px]">{PIPELINE_STAGES.length - hiddenStages.size}/{PIPELINE_STAGES.length}</span>
              <svg className={`w-3 h-3 transition-transform ${stagesDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
            </button>
            {stagesDropdownOpen && (
              <div className="absolute left-0 top-full mt-1 w-52 bg-[#1e1e38] border border-border-subtle rounded-lg shadow-lg overflow-hidden z-50">
                <div className="max-h-72 overflow-y-auto py-1">
                  {PIPELINE_STAGES.map(stage => {
                    const isVisible = !hiddenStages.has(stage.code)
                    return (
                      <button
                        key={stage.code}
                        onClick={() => {
                          setHiddenStages(prev => {
                            const next = new Set(prev)
                            if (next.has(stage.code)) next.delete(stage.code)
                            else next.add(stage.code)
                            return next
                          })
                        }}
                        className="w-full flex items-center gap-2.5 px-3 py-1.5 hover:bg-[#2a2a44] transition-colors"
                      >
                        <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center flex-shrink-0 ${
                          isVisible ? 'bg-primary-red border-primary-red' : 'border-[#4a4a6a]'
                        }`}>
                          {isVisible && (
                            <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                          )}
                        </div>
                        <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: stage.color }} />
                        <span className={`text-[12px] ${isVisible ? 'text-text-primary' : 'text-text-tertiary'}`}>{stage.label}</span>
                      </button>
                    )
                  })}
                </div>
              </div>
            )}
          </div>

          {/* People dropdown — pill style, inline with domain chips */}
          <div className="relative" ref={peopleDropdownRef}>
            <button
              onClick={() => { setPeopleDropdownOpen(v => !v); setStagesDropdownOpen(false) }}
              className={`px-3 py-1 rounded-full text-caption-xs font-semibold transition-colors border flex items-center gap-1.5 ${
                selectedPeople.size > 0
                  ? 'bg-[#2a2a44] text-text-primary border-[#3a3a54] hover:bg-[#333350]'
                  : 'bg-[#2a2a44] text-text-secondary border-[#3a3a54] hover:bg-[#333350]'
              }`}
            >
              People
              {selectedPeople.size > 0 && (
                <span className="text-text-tertiary text-[10px]">{selectedPeople.size}</span>
              )}
              <svg className={`w-3 h-3 transition-transform ${peopleDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
            </button>
            {peopleDropdownOpen && (
              <div className="absolute left-0 top-full mt-1 w-60 bg-[#1e1e38] border border-border-subtle rounded-lg shadow-lg overflow-hidden z-50">
                {selectedPeople.size > 0 && (
                  <button
                    onClick={() => setSelectedPeople(new Set())}
                    className="w-full px-3 py-1.5 text-[11px] text-text-tertiary hover:text-text-secondary hover:bg-[#2a2a44] transition-colors text-left border-b border-border-subtle"
                  >
                    Clear all
                  </button>
                )}
                <div className="max-h-72 overflow-y-auto py-1">
                  {teamMembers.map(member => {
                    const isSelected = selectedPeople.has(member.id)
                    const initials = member.name
                      .split(' ')
                      .slice(0, 2)
                      .map(n => n[0]?.toUpperCase() || '')
                      .join('')
                    return (
                      <button
                        key={member.id}
                        onClick={() => {
                          setSelectedPeople(prev => {
                            const next = new Set(prev)
                            if (next.has(member.id)) next.delete(member.id)
                            else next.add(member.id)
                            return next
                          })
                        }}
                        className="w-full flex items-center gap-2.5 px-3 py-1.5 hover:bg-[#2a2a44] transition-colors"
                      >
                        <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center flex-shrink-0 ${
                          isSelected ? 'bg-primary-red border-primary-red' : 'border-[#4a4a6a]'
                        }`}>
                          {isSelected && (
                            <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                          )}
                        </div>
                        <div
                          className="w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center text-[9px] font-medium"
                          style={{
                            backgroundImage: member.picture_url ? `url('${member.picture_url}')` : undefined,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            backgroundColor: !member.picture_url ? '#4a4a6a' : undefined,
                            color: !member.picture_url ? '#e8e8f0' : undefined,
                          }}
                        >
                          {!member.picture_url && initials}
                        </div>
                        <span className={`text-[12px] truncate ${isSelected ? 'text-text-primary' : 'text-text-secondary'}`}>{member.name}</span>
                      </button>
                    )
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Board */}
      {loading ? (
        <div className="flex-1 overflow-x-auto px-4 py-4">
          <div className="flex gap-4">
            {[0, 1, 2, 3, 4, 5].map(i => (
              <SkeletonColumn key={i} />
            ))}
          </div>
        </div>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <div className="flex-1 overflow-x-auto min-h-0">
            <div className="inline-flex gap-4 p-4 min-w-full h-full">
              {visibleStages.map(stageData => {
                const stageConfig = PIPELINE_MAP[stageData.stage]
                if (!stageConfig) return null

                return (
                  <div
                    key={stageData.stage}
                    className="flex-shrink-0 w-56 flex flex-col bg-bg-body border border-border-subtle rounded-lg max-h-full"
                  >
                    {/* Column Header */}
                    <div className="bg-bg-surface border-b border-border-subtle px-4 py-3 sticky top-0 z-10">
                      <div className="flex items-center justify-between mb-1">
                        <h2 className="text-body font-semibold text-text-primary">
                          {stageData.label}
                        </h2>
                        <span className="inline-flex items-center justify-center min-w-6 h-6 rounded-full bg-bg-body text-caption-xs font-bold text-text-secondary">
                          {stageData.count}
                        </span>
                      </div>
                      {stageData.has_more && (
                        <p className="text-caption-xs text-text-tertiary">
                          {t('pipeline.showing_first_20')}
                        </p>
                      )}
                    </div>

                    {/* Cards Container */}
                    <Droppable droppableId={stageData.stage}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          className={`flex-1 overflow-y-auto px-3 py-2 space-y-2 transition-colors ${
                            snapshot.isDraggingOver ? 'bg-bg-surface' : ''
                          }`}
                        >
                          {stageData.projects.map((card, index) => (
                            <Draggable
                              key={card.id}
                              draggableId={String(card.id)}
                              index={index}
                            >
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  className={`transition-all ${
                                    snapshot.isDragging
                                      ? 'shadow-lg rotate-2 opacity-95'
                                      : ''
                                  }`}
                                >
                                  <MiniCardComponent card={card} onClick={(id) => navigate(`/projects/${id}`)} />
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}

                          {stageData.projects.length === 0 && (
                            <div className="flex items-center justify-center h-24 text-text-tertiary text-caption-xs">
                              {t('pipeline.empty_stage')}
                            </div>
                          )}
                        </div>
                      )}
                    </Droppable>

                    {/* Load more button */}
                    {stageData.has_more && (
                      <div className="px-3 py-2 border-t border-border-subtle">
                        <button
                          onClick={() => handleLoadMore(stageData.stage)}
                          disabled={loadingMore[stageData.stage]}
                          className="w-full px-3 py-2 text-caption-xs font-semibold text-text-secondary bg-bg-surface hover:bg-bg-surface-hover rounded transition-colors disabled:opacity-50"
                        >
                          {loadingMore[stageData.stage]
                            ? t('pipeline.loading_more')
                            : `${t('pipeline.load_more')} (${stageData.count - stageData.projects.length} ${t('pipeline.remaining')})`}
                        </button>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        </DragDropContext>
      )}

      {/* Modals & Toasts */}
      <ReasonModal
        isOpen={reasonModal.isOpen}
        projectTitle={reasonModal.projectTitle}
        stageLabel={PIPELINE_MAP[reasonModal.toStage]?.label || reasonModal.toStage}
        onConfirm={handleReasonConfirm}
        onCancel={handleReasonCancel}
        isSubmitting={isSubmitting}
      />

      {errorMessage && <ErrorToast message={errorMessage} onDismiss={() => setErrorMessage(null)} />}

    </div>
  )
}
